import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import SelectDropdown from "../components-react/SelectDropdown";
import ReactVortoxSpinner from "../common-components/ReactVortoxSpinner";
import {
  fetchFollowupAnalytics,
  postScoreRemark,
  fetchLeadSources,
  downloadFollowupAnalyticsCsv,
} from "../../data/APIs";
import { fetchGymData } from "../../data/APIs";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import { fetchInHouseUsersData } from "../../redux/reducers/dropdownApiSlice";
import { checkIsManagerRole } from "../common-data/Conversions";
import "../../styles/lead-mangement/followup-analytics.css";

const today = () => moment().tz("Asia/Kolkata").format("YYYY-MM-DD");

const PAGE_SIZE = 50;

// ---- pure display helpers (module-level so anywhere in the component
// can reference them without worrying about declaration order / TDZ) ----

const SPECIAL_WORDS = {
  dnb: "DNB",
  btl: "BTL",
  utm: "UTM",
  wtf: "WTF",
  wtt: "WTT",
  pt: "PT",
  fd: "FD",
  ivr: "IVR",
  odm: "ODM",
  // Cosmetic correction for a misspelled DB value (real fix is a DB
  // migration: UPDATE lead_master_v2 SET source_name='triple_nine' WHERE
  // source_name='tripple_nine').
  tripple: "Triple",
};

// Title-case a snake_case identifier for display. Works for follow-up
// statuses (e.g. "missed_followup" -> "Missed Followup"), lead sources
// (e.g. "wtf_one_day_plan" -> "WTF One Day Plan"), and similar.
const titleCase = (s) =>
  String(s || "")
    .split("_")
    .map(
      (w) =>
        SPECIAL_WORDS[w] ||
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");

// Back-compat alias — code still references titleCaseStatus in places.
const titleCaseStatus = titleCase;

// Mask a phone number: keep first 1 digit and last 3, X everything else.
// The page is already admin/manager-only, but masking by default reduces
// shoulder-surfing and casual-screenshot leaks. Per-row reveal via the
// eye button next to the cell.
const maskPhone = (p) => {
  if (!p) return "—";
  const s = String(p).replace(/[^0-9+]/g, "");
  if (s.length <= 4) return s;
  return s.slice(0, 1) + "X".repeat(s.length - 4) + s.slice(-3);
};

const DATE_PRESETS = [
  {
    label: "Today",
    range: () => ({ from: today(), to: today() }),
  },
  {
    label: "Yesterday",
    range: () => {
      const y = moment().tz("Asia/Kolkata").subtract(1, "day").format("YYYY-MM-DD");
      return { from: y, to: y };
    },
  },
  {
    label: "Last 7 days",
    range: () => ({
      from: moment().tz("Asia/Kolkata").subtract(6, "days").format("YYYY-MM-DD"),
      to: today(),
    }),
  },
  {
    label: "Last 30 days",
    range: () => ({
      from: moment().tz("Asia/Kolkata").subtract(29, "days").format("YYYY-MM-DD"),
      to: today(),
    }),
  },
  {
    label: "This month",
    range: () => ({
      from: moment().tz("Asia/Kolkata").startOf("month").format("YYYY-MM-DD"),
      to: today(),
    }),
  },
];

/**
 * Follow-up Analytics — Phase A drill-down workspace.
 *
 * Full-screen analytical view behind any follow-up number on the gym
 * command panel. Visibility-only (no fraud-detection logic yet — that's
 * Phase B). Lets admin / manager roles slice the follow-up log by gym,
 * front-desk staff, date / time, source, and status, see a status
 * breakdown of the filtered set, and paginate through the raw rows.
 */
const FollowupAnalytics = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const gymData = useSelector(selectGymData);
  const { inhouseUsers } = useSelector((s) => s.dropdown);

  const isAdmin =
    userInfoData?.account_type === "admin" ||
    userInfoData?.account_type === "marketing";
  const isManagerRole = checkIsManagerRole(userInfoData);

  // Initial filter values come from the URL so entry-points (e.g. the
  // panel header button or a popup link) can pre-fill the workspace.
  const initial = useMemo(
    () => ({
      gym_id: searchParams.get("gym_id") || "",
      added_by: searchParams.get("added_by") || "",
      source_name: searchParams.get("source_name") || "",
      follow_up_status: searchParams.get("follow_up_status") || "",
      date_from: searchParams.get("date_from") || today(),
      date_to: searchParams.get("date_to") || today(),
      time_from: searchParams.get("time_from") || "",
      time_to: searchParams.get("time_to") || "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Filter state owns the page; URL is mirrored so links are shareable.
  const [filters, setFilters] = useState(initial);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({
    totals: { total_followups: 0, distinct_leads: 0, distinct_staff: 0 },
    breakdown: [],
    rows: [],
    flagged_staff: [],
    thresholds: {},
    pagination: { page: 1, limit: PAGE_SIZE, total: 0 },
  });

  // Phase C — AI authenticity score modal.
  const [ai, setAi] = useState({
    open: false,
    loading: false,
    log_id: null,
    result: null,
    error: "",
  });

  const closeAi = () =>
    setAi({ open: false, loading: false, log_id: null, result: null, error: "" });

  const openAiScore = async (log_id) => {
    setAi({ open: true, loading: true, log_id, result: null, error: "" });
    try {
      const res = await postScoreRemark(log_id, userInfoData?.token);
      if (res?.status) {
        setAi((s) => ({ ...s, loading: false, result: res }));
      } else if (res?.reason === "feature_not_configured") {
        setAi((s) => ({
          ...s,
          loading: false,
          error:
            "AI scoring is not enabled on this API server. Set ANTHROPIC_API_KEY in the API env to turn it on.",
        }));
      } else {
        setAi((s) => ({
          ...s,
          loading: false,
          error: res?.message || "AI call failed.",
        }));
      }
    } catch (e) {
      setAi((s) => ({
        ...s,
        loading: false,
        error: "Network error while calling the AI service.",
      }));
    }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  // Per-row reveal of masked phone numbers, keyed by log-row uid.
  const [revealedPhones, setRevealedPhones] = useState({});
  const toggleReveal = (uid) =>
    setRevealedPhones((r) => ({ ...r, [uid]: !r[uid] }));

  // Selected dropdown values (the SelectDropdown component expects
  // {label, value} pairs but our filter state stores plain ids).
  const [gymOpt, setGymOpt] = useState(null);
  const [fdOpt, setFdOpt] = useState(null);
  const [srcOpt, setSrcOpt] = useState(null);

  // Lead source options — fetched once on mount (lifetime distinct values
  // from lead_master_v2), formatted as { label, value } for SelectDropdown.
  const [sourceOpts, setSourceOpts] = useState([]);

  // Column sort state for the two client-side tables. `col: null` means
  // "use the server's default order" (status-known-order for breakdown,
  // total_flag_instances DESC for staff).
  const [bdSort, setBdSort] = useState({ col: null, dir: "desc" });
  const [staffSort, setStaffSort] = useState({ col: null, dir: "desc" });

  // Jump-to-page input (separate from `page` so typing doesn't fire a
  // query on every keystroke).
  const [pageInput, setPageInput] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  // Ref to the Follow-up Log section; the Total chip scrolls to it.
  const logSectionRef = useRef(null);
  const scrollToLog = () =>
    logSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  // Sync selected option back to filters.
  const onGymChange = (opt) => {
    setGymOpt(opt);
    setFilters((f) => ({ ...f, gym_id: opt?.value || "" }));
    setPage(1);
  };
  const onFdChange = (opt) => {
    setFdOpt(opt);
    setFilters((f) => ({ ...f, added_by: opt?.value || "" }));
    setPage(1);
  };

  const onSourceChange = (opt) => {
    setSrcOpt(opt);
    setFilters((f) => ({ ...f, source_name: opt?.value || "" }));
    setPage(1);
  };

  // Hydrate gym + in-house users dropdowns (admin / manager only).
  useEffect(() => {
    if (isAdmin || isManagerRole) {
      dispatch(fetchGymData(userInfoData?.token));
    }
  }, [dispatch, isAdmin, isManagerRole, userInfoData?.token]);

  useEffect(() => {
    dispatch(
      fetchInHouseUsersData({
        token: userInfoData?.token,
        qps: filters.gym_id ? `gym_id=${filters.gym_id}` : "",
        pageNo: 1,
        limit: 100,
      }),
    );
  }, [dispatch, filters.gym_id, userInfoData?.token]);

  // Resolve initial gym/fd labels once dropdowns load (so a deep-link with
  // gym_id=... shows the gym name, not just the id).
  useEffect(() => {
    if (filters.gym_id && !gymOpt && gymData?.length) {
      const hit = gymData.find((g) => g.value === filters.gym_id);
      if (hit) setGymOpt(hit);
    }
  }, [filters.gym_id, gymOpt, gymData]);

  useEffect(() => {
    if (filters.added_by && !fdOpt && inhouseUsers?.length) {
      const hit = inhouseUsers.find((u) => u.value === filters.added_by);
      if (hit) setFdOpt(hit);
    }
  }, [filters.added_by, fdOpt, inhouseUsers]);

  // Load lead source list once. Lifetime distinct values — doesn't
  // change when the date range moves.
  useEffect(() => {
    if (!userInfoData?.token) return;
    (async () => {
      try {
        const res = await fetchLeadSources(userInfoData.token);
        if (res?.status && Array.isArray(res.sources)) {
          setSourceOpts(
            res.sources.map((s) => ({ label: titleCase(s), value: s })),
          );
        }
      } catch (e) {
        // Source dropdown is non-critical; the filter still works as a
        // typed value via the URL.
      }
    })();
  }, [userInfoData?.token]);

  // Hydrate source dropdown from a deep-linked filter value.
  useEffect(() => {
    if (filters.source_name && !srcOpt && sourceOpts.length) {
      const hit = sourceOpts.find((o) => o.value === filters.source_name);
      if (hit) setSrcOpt(hit);
    }
  }, [filters.source_name, srcOpt, sourceOpts]);

  // Mirror filters to URL (shareable links + browser-back works).
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const loadData = useCallback(async () => {
    if (!userInfoData?.token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchFollowupAnalytics(
        { ...filters, page, limit: PAGE_SIZE },
        userInfoData.token,
      );
      if (res?.status) {
        setData({
          totals: res.totals || {},
          breakdown: res.breakdown || [],
          rows: res.rows || [],
          flagged_staff: res.flagged_staff || [],
          thresholds: res.thresholds || {},
          pagination: res.pagination || { page, limit: PAGE_SIZE, total: 0 },
        });
        setLastUpdated(new Date());
      } else {
        setError(res?.message || "Could not load analytics.");
      }
    } catch (e) {
      setError("Could not load analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, page, userInfoData?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onReset = () => {
    setFilters({
      gym_id: "",
      added_by: "",
      source_name: "",
      follow_up_status: "",
      date_from: today(),
      date_to: today(),
      time_from: "",
      time_to: "",
    });
    setGymOpt(null);
    setFdOpt(null);
    setSrcOpt(null);
    setPage(1);
    setBdSort({ col: null, dir: "desc" });
    setStaffSort({ col: null, dir: "desc" });
  };

  // Toggle a column header click for a sortable table. First click on a
  // new column sorts DESC; clicking the active column flips direction.
  const toggleSort = (setSort, col) =>
    setSort((s) => ({
      col,
      dir: s.col === col ? (s.dir === "asc" ? "desc" : "asc") : "desc",
    }));

  // Read a possibly-nested value from a row (e.g. "flags.duplicate_remarks").
  const getCell = (row, col) =>
    col.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), row);

  // Sort a row array by {col, dir}. Strings sorted case-insensitively;
  // numbers and percentages by numeric value.
  const sortBy = (rows, sort) => {
    if (!sort.col) return rows;
    const out = [...rows];
    out.sort((a, b) => {
      const va = getCell(a, sort.col);
      const vb = getCell(b, sort.col);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sort.dir === "asc" ? va - vb : vb - va;
      }
      const na = Number(va);
      const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) {
        return sort.dir === "asc" ? na - nb : nb - na;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sort.dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return out;
  };

  // Sort indicator glyph for a column header.
  const sortIndicator = (sort, col) => {
    if (sort.col !== col) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const onExport = async () => {
    if (!userInfoData?.token) return;
    setExportLoading(true);
    try {
      const blob = await downloadFollowupAnalyticsCsv(
        filters,
        userInfoData.token,
      );
      const stamp = moment()
        .tz("Asia/Kolkata")
        .format("YYYY-MM-DD_HH-mm");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `followup-analytics_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const onJumpToPage = () => {
    const p = parseInt(pageInput, 10);
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setPage(p);
      setPageInput("");
    }
  };

  const setField = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  // Status dropdown options — union of known + whatever the breakdown
  // returns, so custom statuses present in the data appear too.
  const statusOptions = useMemo(() => {
    // 'pending' rows are re-bucketed by the backend as upcoming/missed
    // (based on scheduled_on vs now) — list those synthetic buckets here
    // so they show at the top of the filter dropdown.
    const known = [
      "missed_followup",
      "upcoming_followup",
      "dnb",
      "not_interested",
      "interested",
      "follow_up_later",
      "converted",
      "switched_off",
      "invalid",
      "trial_planned",
      "trial_done",
      "visit_scheduled",
      "visit_done",
      "closure_expected",
      "gym_visit",
      "payment_link_sent",
    ];
    const fromData = data.breakdown
      .map((b) => String(b.status || "").toLowerCase())
      .filter((s) => s && s !== "(none)");
    const all = Array.from(new Set([...known, ...fromData]));
    const countByStatus = {};
    data.breakdown.forEach((r) => {
      const key = String(r.status || "").toLowerCase();
      countByStatus[key] = Number(r.cnt) || 0;
    });
    return [{ label: "All statuses", value: "" }].concat(
      all.map((s) => {
        const cnt = countByStatus[s] || 0;
        const cntLabel = cnt ? ` (${cnt.toLocaleString("en-IN")})` : "";
        return { label: `${titleCaseStatus(s)}${cntLabel}`, value: s };
      }),
    );
  }, [data.breakdown]);

  const totalPages = Math.max(
    1,
    Math.ceil((data.pagination.total || 0) / PAGE_SIZE),
  );

  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

  // "YYYY-MM-DD" -> "DD-MM-YYYY"; filter values stay ISO under the hood.
  const displayDate = (s) => {
    if (!s) return "—";
    const ymd = String(s).slice(0, 10);
    const [y, m, d] = ymd.split("-");
    return y && m && d ? `${d}-${m}-${y}` : ymd;
  };

  const fmtTime = (s) => {
    if (!s) return "—";
    const str = String(s).replace("T", " ");
    const ymd = str.slice(0, 10);
    const hm = str.slice(11, 16); // "HH:MM" (24h)
    const [y, m, d] = ymd.split("-");
    if (!y || !m || !d) return str.slice(0, 16);
    if (!hm) return `${d}-${m}-${y}`;
    const [h24, mm] = hm.split(":");
    const h = parseInt(h24, 10);
    if (isNaN(h)) return `${d}-${m}-${y} ${hm}`;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${d}-${m}-${y} ${h12}:${mm} ${period}`;
  };

  // Phase B flag metadata — keep the keys in sync with the backend.
  const FLAG_META = {
    duplicate_remarks: {
      label: "Duplicate",
      tip: `Same remark used by this staff ≥${
        data.thresholds.duplicate_remarks_min || 3
      } times today.`,
    },
    status_thrash: {
      label: "Thrash",
      tip: `Same lead toggled across ≥${
        data.thresholds.status_thrash_min || 3
      } distinct statuses today.`,
    },
    short_remark: {
      label: "Short",
      tip: `Remark length ≤ ${
        data.thresholds.short_remark_max_len || 5
      } characters.`,
    },
    low_distinct_ratio: {
      label: "Low distinct",
      tip: `Staff's distinct-leads / total-rows < ${
        data.thresholds.low_distinct_pct || 50
      }% — repeated touching of few leads.`,
    },
  };

  return (
    <div className="fa-page">
      <div className="fa-page__head">
        <div>
          <button
            type="button"
            className="fa-back"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <h2 className="fa-title">Follow-up Analytics</h2>
          <p className="fa-sub">
            Phase A · visibility · {displayDate(filters.date_from)} →{" "}
            {displayDate(filters.date_to)}
          </p>
        </div>
        <button
          type="button"
          className="fa-btn fa-btn--ghost"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* ---- filters ---- */}
      <div className="fa-filters">
        <div className="fa-field">
          <label>Gym</label>
          <SelectDropdown
            options={gymData?.length ? gymData : []}
            selectedOption={gymOpt}
            setSelectedOption={onGymChange}
            name="All gyms"
          />
        </div>

        <div className="fa-field">
          <label>Front-desk executive</label>
          <SelectDropdown
            options={
              inhouseUsers?.length
                ? inhouseUsers.filter(
                    (u) => !String(u?.label || "").includes("trainer"),
                  )
                : []
            }
            selectedOption={fdOpt}
            setSelectedOption={onFdChange}
            name="All staff"
          />
        </div>

        <div className="fa-field">
          <label>Date from</label>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setField("date_from", e.target.value)}
          />
        </div>
        <div className="fa-field">
          <label>Date to</label>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setField("date_to", e.target.value)}
          />
        </div>

        <div className="fa-field">
          <label
            title="Leave blank for full day. Time filters apply to the moment the follow-up was actioned (last_updated)."
          >
            Time from
          </label>
          <input
            type="time"
            value={filters.time_from}
            placeholder="full day"
            onChange={(e) => setField("time_from", e.target.value)}
          />
        </div>
        <div className="fa-field">
          <label>Time to</label>
          <input
            type="time"
            value={filters.time_to}
            placeholder="full day"
            onChange={(e) => setField("time_to", e.target.value)}
          />
        </div>

        <div className="fa-field">
          <label>Lead source</label>
          <SelectDropdown
            options={sourceOpts}
            selectedOption={srcOpt}
            setSelectedOption={onSourceChange}
            name="All sources"
          />
        </div>

        <div className="fa-field">
          <label>Follow-up status</label>
          <select
            value={filters.follow_up_status}
            onChange={(e) => setField("follow_up_status", e.target.value)}
            title={
              statusOptions.find((o) => o.value === filters.follow_up_status)
                ?.label || "All statuses"
            }
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value} title={o.label}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="fa-field fa-field--actions">
          <button
            type="button"
            className="fa-btn fa-btn--ghost"
            onClick={onReset}
            title="Clear all filters and reset to today"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Date range presets — one click instead of two pickers. */}
      <div className="fa-presets">
        <span className="fa-presets__label">Quick range:</span>
        {DATE_PRESETS.map((p) => {
          const r = p.range();
          const active =
            filters.date_from === r.from && filters.date_to === r.to;
          return (
            <button
              key={p.label}
              type="button"
              className={`fa-preset ${active ? "is-active" : ""}`}
              onClick={() => {
                setFilters((f) => ({
                  ...f,
                  date_from: r.from,
                  date_to: r.to,
                }));
                setPage(1);
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {lastUpdated && (
        <div className="fa-last-updated">
          Last updated:{" "}
          {lastUpdated.toLocaleTimeString("en-IN", {
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })}
          {loading ? "  ·  refreshing…" : ""}
        </div>
      )}

      {/* ---- totals ---- */}
      <div className="fa-totals">
        <div
          className="fa-chip fa-chip--blue fa-chip--clickable"
          onClick={scrollToLog}
          title="Scroll to the follow-up log below"
        >
          <span className="fa-chip__value">
            {fmt(data.totals.total_followups)}
          </span>
          <span className="fa-chip__label">Total follow-ups</span>
        </div>
        <div className="fa-chip fa-chip--teal">
          <span className="fa-chip__value">
            {fmt(data.totals.distinct_leads)}
          </span>
          <span className="fa-chip__label">Distinct leads</span>
        </div>
        <div className="fa-chip fa-chip--purple">
          <span className="fa-chip__value">
            {fmt(data.totals.distinct_staff)}
          </span>
          <span className="fa-chip__label">Distinct staff</span>
        </div>
      </div>

      {error && <div className="fa-error">{error}</div>}

      {/* ---- breakdown ---- */}
      <section className="fa-section">
        <header className="fa-section__head">
          <h3>Status Breakdown</h3>
          <span className="fa-section__hint">
            Click a row to filter the log below.
          </span>
        </header>
        {loading && !data.breakdown.length ? (
          <ReactVortoxSpinner />
        ) : data.breakdown.length === 0 ? (
          <div className="fa-empty">No follow-ups for these filters.</div>
        ) : (
          <table className="fa-table">
            <thead>
              <tr>
                <th
                  className="fa-th-sort"
                  onClick={() => toggleSort(setBdSort, "status")}
                  title="Click to sort by status name"
                >
                  Status{sortIndicator(bdSort, "status")}
                </th>
                <th
                  className="num fa-th-sort"
                  onClick={() => toggleSort(setBdSort, "cnt")}
                  title="Click to sort by count"
                >
                  Count{sortIndicator(bdSort, "cnt")}
                </th>
                <th
                  className="num fa-th-sort"
                  onClick={() => toggleSort(setBdSort, "pct")}
                  title="Click to sort by percentage"
                >
                  %{sortIndicator(bdSort, "pct")}
                </th>
                <th
                  className="num fa-th-sort"
                  onClick={() => toggleSort(setBdSort, "distinct_leads")}
                  title="Click to sort by distinct leads"
                >
                  Distinct leads{sortIndicator(bdSort, "distinct_leads")}
                </th>
                <th
                  className="num fa-th-sort"
                  onClick={() => toggleSort(setBdSort, "distinct_staff")}
                  title="Click to sort by distinct staff"
                >
                  Distinct staff{sortIndicator(bdSort, "distinct_staff")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortBy(data.breakdown, bdSort).map((row) => {
                const status = String(row.status).toLowerCase();
                const isUpcoming = status === "upcoming_followup";
                const active = filters.follow_up_status === status;
                return (
                  <tr
                    key={row.status}
                    className={`fa-bd-row ${active ? "is-active" : ""}`}
                    title={
                      isUpcoming
                        ? "Click to filter the log to scheduled-but-not-yet-actioned follow-ups. Rows show full lead + scheduled-on info; remarks are empty by design."
                        : "Click to filter the log below by this status"
                    }
                    onClick={() =>
                      setField("follow_up_status", active ? "" : status)
                    }
                  >
                    <td className="fa-status">
                      {titleCaseStatus(row.status)}
                    </td>
                    <td className="num">{fmt(row.cnt)}</td>
                    <td className="num">{row.pct}%</td>
                    <td className="num">{fmt(row.distinct_leads)}</td>
                    <td className="num">{fmt(row.distinct_staff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- suspicious staff & flag density (Phase B) ---- */}
      <section className="fa-section">
        <header className="fa-section__head">
          <h3>Suspicious Staff & Flag Density</h3>
          <span className="fa-section__hint">
            Heuristic-only · staff with at least one flag instance in the
            filtered window, ranked by total instances.
          </span>
        </header>
        {data.flagged_staff.length === 0 ? (
          <div className="fa-empty">
            No flagged activity in the current filter window.
          </div>
        ) : (
          <div className="fa-table-wrap">
            <table className="fa-table">
              <thead>
                <tr>
                  <th
                    className="fa-th-sort"
                    onClick={() => toggleSort(setStaffSort, "user_name")}
                  >
                    Staff{sortIndicator(staffSort, "user_name")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title="All follow-up log rows this staff added in the filter window"
                    onClick={() => toggleSort(setStaffSort, "total_rows")}
                  >
                    Total rows{sortIndicator(staffSort, "total_rows")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title="How many different leads this staff touched (a lead followed up 5 times still counts as 1)"
                    onClick={() => toggleSort(setStaffSort, "distinct_leads")}
                  >
                    Distinct leads
                    {sortIndicator(staffSort, "distinct_leads")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title={`distinct_leads ÷ total_rows. Red when below ${
                      data.thresholds.low_distinct_pct || 50
                    }% — staff is touching the same few leads repeatedly.`}
                    onClick={() => toggleSort(setStaffSort, "distinct_pct")}
                  >
                    Distinct %{sortIndicator(staffSort, "distinct_pct")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title={`Rows flagged for duplicate remarks (same remark text used ≥${
                      data.thresholds.duplicate_remarks_min || 3
                    } times by this staff today)`}
                    onClick={() =>
                      toggleSort(setStaffSort, "flags.duplicate_remarks")
                    }
                  >
                    Duplicate
                    {sortIndicator(staffSort, "flags.duplicate_remarks")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title={`Rows flagged for status thrash (same lead toggled across ≥${
                      data.thresholds.status_thrash_min || 3
                    } distinct statuses by this staff today)`}
                    onClick={() =>
                      toggleSort(setStaffSort, "flags.status_thrash")
                    }
                  >
                    Thrash{sortIndicator(staffSort, "flags.status_thrash")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title={`Rows with very short or empty remarks (length ≤ ${
                      data.thresholds.short_remark_max_len || 5
                    } chars)`}
                    onClick={() =>
                      toggleSort(setStaffSort, "flags.short_remark")
                    }
                  >
                    Short{sortIndicator(staffSort, "flags.short_remark")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title="If this staff's distinct % is below threshold, every one of their rows gets flagged"
                    onClick={() =>
                      toggleSort(setStaffSort, "flags.low_distinct_ratio")
                    }
                  >
                    Low-distinct
                    {sortIndicator(staffSort, "flags.low_distinct_ratio")}
                  </th>
                  <th
                    className="num fa-th-sort"
                    title="Sum across all flag columns. NOT a row count — a row can be flagged for more than one reason."
                    onClick={() =>
                      toggleSort(setStaffSort, "total_flag_instances")
                    }
                  >
                    Total instances
                    {sortIndicator(staffSort, "total_flag_instances")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortBy(data.flagged_staff, staffSort).map((s) => (
                  <tr
                    key={s.user_id}
                    className="fa-bd-row"
                    onClick={() => {
                      setField("added_by", s.user_id);
                      setFdOpt({ label: s.user_name, value: s.user_id });
                    }}
                    title="Click to filter the log below to this staff member"
                  >
                    <td>
                      <span className="fa-staff-link">{s.user_name}</span>
                    </td>
                    <td className="num">{fmt(s.total_rows)}</td>
                    <td className="num">{fmt(s.distinct_leads)}</td>
                    <td
                      className={`num ${
                        s.distinct_pct < (data.thresholds.low_distinct_pct || 50)
                          ? "fa-cell-warn"
                          : ""
                      }`}
                      title={
                        s.distinct_pct < (data.thresholds.low_distinct_pct || 50)
                          ? `Below ${data.thresholds.low_distinct_pct || 50}% threshold`
                          : ""
                      }
                    >
                      {s.distinct_pct}%
                    </td>
                    <td className="num">{fmt(s.flags.duplicate_remarks)}</td>
                    <td className="num">{fmt(s.flags.status_thrash)}</td>
                    <td className="num">{fmt(s.flags.short_remark)}</td>
                    <td className="num">{fmt(s.flags.low_distinct_ratio)}</td>
                    <td className="num">
                      <strong>{fmt(s.total_flag_instances)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---- raw rows ---- */}
      <section className="fa-section" ref={logSectionRef}>
        <header className="fa-section__head fa-section__head--with-actions">
          <div>
            <h3>Follow-up Log</h3>
            <span className="fa-section__hint">
              Showing {fmt(data.rows.length)} of {fmt(data.pagination.total)}{" "}
              rows · page {page} / {totalPages}
            </span>
          </div>
          <button
            type="button"
            className="fa-btn fa-btn--ghost"
            onClick={onExport}
            disabled={exportLoading || data.pagination.total === 0}
            title="Download the full filtered log as a CSV (up to 50,000 rows)"
          >
            {exportLoading ? "Exporting…" : "Export CSV"}
          </button>
        </header>
        {loading && !data.rows.length ? (
          <ReactVortoxSpinner />
        ) : data.rows.length === 0 ? (
          <div className="fa-empty">No rows for these filters.</div>
        ) : (
          <>
            <div className="fa-table-wrap">
              <table className="fa-table fa-table--rows">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Last updated</th>
                    <th>Status</th>
                    <th>Lead</th>
                    <th>Phone</th>
                    <th>Gym</th>
                    <th>Source</th>
                    <th>Added by</th>
                    <th>Flags</th>
                    <th>Remarks</th>
                    <th>AI</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr
                      key={r.uid}
                      className={
                        r.flags && r.flags.length ? "fa-row-flagged" : ""
                      }
                    >
                      <td className="num">
                        {(page - 1) * PAGE_SIZE + i + 1}
                      </td>
                      <td>{fmtTime(r.last_updated)}</td>
                      <td className="fa-status">
                        {titleCaseStatus(r.follow_up_status || "—")}
                      </td>
                      <td>{r.lead_name || "—"}</td>
                      <td>
                        <span className="fa-phone">
                          {revealedPhones[r.uid]
                            ? r.lead_mobile || "—"
                            : maskPhone(r.lead_mobile)}
                        </span>
                        {r.lead_mobile && (
                          <button
                            type="button"
                            className="fa-phone-toggle"
                            onClick={() => toggleReveal(r.uid)}
                            title={
                              revealedPhones[r.uid]
                                ? "Hide phone number"
                                : "Reveal full phone number"
                            }
                          >
                            {revealedPhones[r.uid] ? "Hide" : "Show"}
                          </button>
                        )}
                      </td>
                      <td>{r.gym_name || "—"}</td>
                      <td>{r.source_name ? titleCase(r.source_name) : "—"}</td>
                      <td>{r.added_by_name || "—"}</td>
                      <td>
                        {r.flags && r.flags.length ? (
                          <div className="fa-flag-cell">
                            {r.flags.map((f) => (
                              <span
                                key={f}
                                className={`fa-flag fa-flag--${f}`}
                                title={
                                  FLAG_META[f]
                                    ? FLAG_META[f].tip
                                    : f.replace(/_/g, " ")
                                }
                              >
                                {FLAG_META[f]
                                  ? FLAG_META[f].label
                                  : f.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="fa-flag-none">—</span>
                        )}
                      </td>
                      <td className="fa-remarks">{r.remarks || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="fa-ai-btn"
                          onClick={() => openAiScore(r.uid)}
                          title="Send this remark to Claude for an authenticity score"
                        >
                          Score
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fa-pagination">
              <button
                type="button"
                className="fa-btn fa-btn--ghost"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                title="Jump to first page"
              >
                « First
              </button>
              <button
                type="button"
                className="fa-btn fa-btn--ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                ← Prev
              </button>
              <span>
                Page <strong>{page}</strong> of {totalPages}
              </span>
              <button
                type="button"
                className="fa-btn fa-btn--ghost"
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={page >= totalPages || loading}
              >
                Next →
              </button>
              <button
                type="button"
                className="fa-btn fa-btn--ghost"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                title="Jump to last page"
              >
                Last »
              </button>
              <span className="fa-jump">
                <span className="fa-jump__label">Go to page</span>
                <input
                  type="number"
                  className="fa-jump__input"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  placeholder={`1–${totalPages}`}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onJumpToPage();
                  }}
                />
                <button
                  type="button"
                  className="fa-btn fa-btn--ghost"
                  onClick={onJumpToPage}
                  disabled={!pageInput}
                >
                  Go
                </button>
              </span>
            </div>
          </>
        )}
      </section>

      {/* ---- Phase C: AI authenticity score modal ---- */}
      {ai.open && (
        <div className="fa-ai-overlay" onClick={closeAi}>
          <div
            className="fa-ai-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fa-ai-modal__head">
              <h4>AI Authenticity Score</h4>
              <button
                type="button"
                className="fa-ai-modal__close"
                onClick={closeAi}
              >
                ✕
              </button>
            </div>
            <div className="fa-ai-modal__body">
              {ai.loading ? (
                <ReactVortoxSpinner />
              ) : ai.error ? (
                <div className="fa-error">{ai.error}</div>
              ) : ai.result ? (
                <>
                  <div
                    className={`fa-ai-score fa-ai-score--${ai.result.label}`}
                  >
                    <div className="fa-ai-score__num">{ai.result.score}</div>
                    <div className="fa-ai-score__label">
                      {ai.result.label}
                    </div>
                  </div>
                  <p className="fa-ai-reason">{ai.result.reason}</p>
                  <div className="fa-ai-context">
                    <div>
                      <strong>Remark sent:</strong>{" "}
                      <span className="fa-ai-quote">
                        {ai.result.input_preview?.remark || "(empty)"}
                      </span>
                    </div>
                    <div>
                      <strong>Status:</strong>{" "}
                      {ai.result.input_preview?.follow_up_status || "—"}
                    </div>
                    <div>
                      <strong>Source:</strong>{" "}
                      {ai.result.input_preview?.source_name || "—"}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowupAnalytics;
